// EPG start notifications — the PURE planner (Spec 10, ported to Vue).
//
// Port of `legacy-public/js/components/EpgNotifier.js`'s `_schedulable()`
// (lines 33-45), which the legacy class deliberately hung off the
// constructor (`window.EpgNotifier._schedulable`, line 206) so it could be
// unit-tested in node without timers, permissions or a Notification
// constructor. Same split here, enforced by the module boundary: this file
// decides WHAT to notify about and WHEN; `composables/useEpgNotifications.ts`
// owns the timers, the permission handshake and the Notification itself.
//
// Nothing in this module touches `Date.now()`, `window`, or any store — the
// caller passes `now` in. That is what makes the horizon / lead-time /
// dedupe boundaries testable without fake timers.

/**
 * Only programmes STARTING within this window are scheduled.
 * Legacy constant: `HORIZON_MS = 10 * 60 * 1000` (EpgNotifier.js:21).
 */
export const EPG_NOTIFICATION_HORIZON_MS = 10 * 60 * 1000;

/**
 * Fire this far BEFORE the programme starts.
 * Legacy constant: `LEAD_MS = 60 * 1000` (EpgNotifier.js:22).
 */
export const EPG_NOTIFICATION_LEAD_MS = 60 * 1000;

/**
 * How often the scheduler re-plans — DERIVED from the two constants above
 * rather than written as a third literal (CLAUDE.md field-sweep rule #2:
 * "two numbers describing one fact must be derived from each other").
 *
 * The derivation is a coverage proof, not a taste call. A programme starting
 * at `S` is armed by a re-plan running at `T` iff BOTH filters below pass:
 *
 *     fireAt > T      ⇒  T <  S - LEAD
 *     S - T <= HORIZON ⇒  T >= S - HORIZON
 *
 * i.e. exactly when `T ∈ [S - HORIZON, S - LEAD)`, a half-open interval of
 * length `HORIZON - LEAD`. Re-plans spaced by that same length therefore put
 * exactly one re-plan inside it, for every possible `S`.
 *
 * The legacy notifier re-planned every `10 min` — its full HORIZON
 * (EpgNotifier.js:74) — which is `LEAD` too long: a programme starting just
 * past the horizon of one tick landed inside the lead window of the next and
 * was dropped by BOTH. Any programme starting in a 60-second band once every
 * 10 minutes silently produced no notification. Deriving the cadence closes
 * that band.
 */
export const EPG_NOTIFICATION_REPLAN_INTERVAL_MS =
    EPG_NOTIFICATION_HORIZON_MS - EPG_NOTIFICATION_LEAD_MS;

/**
 * A programme as the planner needs it. Deliberately narrower than the EPG
 * store's `ProgrammeWithSource`: only the fields the decision reads.
 *
 * `sourceId` / `channelId` are the LIVE channel's identity, not the XMLTV
 * one — `POST /api/proxy/epg/window` returns `sourceId` = live source id and
 * `channelId` = live item id (see `EpgWindowProgramme` in `lib/api.ts`), so
 * `(sourceId, channelId)` is the same pair that composes a `liveId` and the
 * same pair favourites are keyed by.
 */
export interface PlannableProgramme {
    sourceId: number;
    channelId: string;
    startMs: number;
    title: string;
    /** Display name of the channel, for the notification body. Optional — the body degrades. */
    channelName?: string | null;
}

/** One armed notification: what to show, and the instant to show it. */
export interface EpgNotificationPlanEntry {
    /** `${sourceId}:${channelId}:${startMs}` — the dedupe identity AND the Notification `tag`. */
    key: string;
    sourceId: number;
    channelId: string;
    /** `${sourceId}:${channelId}` — the composite live id the rest of the app routes by. */
    liveId: string;
    startMs: number;
    /** Epoch ms at which the notification should be shown (`startMs - leadMs`). */
    fireAt: number;
    title: string;
    channelName: string | null;
}

export interface EpgNotificationPlanInput {
    programmes: readonly PlannableProgramme[];
    /**
     * `liveKey()` of every channel the user has favourited. Re-checked HERE
     * and not only at fetch time: favourites can change between the fetch
     * that produced `programmes` and the moment we arm timers, and a
     * notification for a channel the user just un-favourited is a bug the
     * fetch scope alone cannot prevent.
     */
    favouriteLiveKeys: ReadonlySet<string>;
    now: number;
    /**
     * Keys already NOTIFIED. Re-plans deliberately re-arm everything still
     * pending (timers are cancelled and rebuilt each pass so the set always
     * reflects the newest programme data), so the only thing that must never
     * come back is a programme whose notification has already been shown.
     */
    alreadyNotified?: ReadonlySet<string>;
    /** Overrides for tests. Non-finite values fall back to the constants above. */
    leadMs?: number;
    horizonMs?: number;
}

/**
 * The canonical live-channel key for this feature. Composite `sourceId:itemId`
 * ids are formed inline in plenty of older call sites; every layer of the
 * notification path goes through THIS one so the favourite set, the programme
 * key and the deep link cannot drift apart (CLAUDE.md rule #2).
 */
export function liveKey(sourceId: number, channelId: string): string {
    return `${sourceId}:${channelId}`;
}

/**
 * The canonical notification identity: the FULL tuple downstream behaviour
 * distinguishes on (CLAUDE.md rule #4). `stopMs` is deliberately NOT part of
 * it — the notification is about the START instant, so two feed rows that
 * differ only in their end time are duplicate announcements of one event and
 * must collapse to a single notification.
 */
export function programmeKey(sourceId: number, channelId: string, startMs: number): string {
    return `${liveKey(sourceId, channelId)}:${startMs}`;
}

/** `/live` deep link for one channel. `Live.vue`'s `consumeDeepLink()` consumes exactly these two params. */
export function liveDeepLink(sourceId: number, channelId: string): string {
    return `/live?source=${encodeURIComponent(String(sourceId))}&channel=${encodeURIComponent(channelId)}`;
}

/**
 * Where a notification click should land. ONE resolver shared by both click
 * paths — the service worker's `notificationclick` relay (`main.ts`) and the
 * in-page `Notification.onclick` fallback used when no service worker is
 * active (Vite dev, or a failed registration).
 *
 * Only app-relative paths are honoured. The data rides through
 * `postMessage` from the SW, and a message handler that pushed an arbitrary
 * string into the router would be an open redirect the moment anything else
 * could post it.
 */
export function notificationClickTarget(data: unknown): string {
    const url = (data as { url?: unknown } | null | undefined)?.url;
    if (typeof url !== 'string') return '/live';
    if (!url.startsWith('/') || url.startsWith('//')) return '/live';
    return url;
}

/**
 * Human phrasing for a duration, DERIVED from the millisecond value so the
 * copy cannot drift from the number that actually governs the timer (the
 * legacy body text hard-coded "starts in 1 minute" next to a configurable
 * `LEAD_MS`).
 */
export function leadPhrase(leadMs: number = EPG_NOTIFICATION_LEAD_MS): string {
    const ms = Number.isFinite(leadMs) ? Math.max(0, leadMs) : EPG_NOTIFICATION_LEAD_MS;
    if (ms < 60_000) {
        const seconds = Math.max(1, Math.round(ms / 1000));
        return `in ${seconds} second${seconds === 1 ? '' : 's'}`;
    }
    const minutes = Math.round(ms / 60_000);
    return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * What the notification body should say about the wait, measured AT FIRE TIME
 * rather than assumed from the lead constant.
 *
 * The timer is armed up to nine minutes ahead in a tab the user is by
 * definition not looking at — which is exactly where browsers apply intensive
 * throttling to hidden pages and align timer wakes to roughly one a minute. A
 * body built from `leadPhrase()` would keep claiming "in 1 minute" on a wake
 * that arrived late, which is a small lie told at the one moment the user is
 * reading it. Deriving from the real remaining time degrades honestly instead.
 */
export function startsInPhrase(startMs: number, now: number): string {
    if (!Number.isFinite(startMs) || !Number.isFinite(now)) return `starts ${leadPhrase()}`;
    const remaining = startMs - now;
    if (remaining <= 5_000) return 'starting now';
    return `starts ${leadPhrase(remaining)}`;
}

function finiteOr(value: number | undefined, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Pure: decide which programmes get a notification, and when.
 *
 * Filters, in order (every one of them has a test that fails if removed):
 *   1. malformed rows — missing/blank `channelId`, non-finite `sourceId`,
 *      non-finite `startMs`. `Date.parse('garbage')` is `NaN` and a `NaN`
 *      timer delay degenerates to 0 (CLAUDE.md rule #10), so unparseable
 *      times are dropped rather than fired immediately.
 *   2. channels the user has not favourited.
 *   3. duplicates — within this batch, and against `alreadyNotified`.
 *   4. too late: `fireAt <= now` (already started, or inside the lead window).
 *   5. too far: `startMs - now > horizon`.
 *
 * Returned in `fireAt` order (key as tie-break) so the plan is deterministic
 * for a given input regardless of feed row order.
 */
export function planEpgNotifications(input: EpgNotificationPlanInput): EpgNotificationPlanEntry[] {
    const now = input.now;
    if (!Number.isFinite(now)) return [];
    const leadMs = finiteOr(input.leadMs, EPG_NOTIFICATION_LEAD_MS);
    const horizonMs = finiteOr(input.horizonMs, EPG_NOTIFICATION_HORIZON_MS);

    const out: EpgNotificationPlanEntry[] = [];
    const seen = new Set<string>();

    for (const p of input.programmes) {
        if (!p) continue;
        if (typeof p.channelId !== 'string' || p.channelId.length === 0) continue;
        if (typeof p.sourceId !== 'number' || !Number.isFinite(p.sourceId)) continue;
        if (typeof p.startMs !== 'number' || !Number.isFinite(p.startMs)) continue;

        const lk = liveKey(p.sourceId, p.channelId);
        if (!input.favouriteLiveKeys.has(lk)) continue;

        const key = programmeKey(p.sourceId, p.channelId, p.startMs);
        if (seen.has(key)) continue;
        if (input.alreadyNotified?.has(key)) continue;

        const fireAt = p.startMs - leadMs;
        if (fireAt <= now) continue;
        if (p.startMs - now > horizonMs) continue;

        seen.add(key);
        out.push({
            key,
            sourceId: p.sourceId,
            channelId: p.channelId,
            liveId: lk,
            startMs: p.startMs,
            fireAt,
            title: typeof p.title === 'string' && p.title.length > 0 ? p.title : 'Upcoming programme',
            channelName: typeof p.channelName === 'string' && p.channelName.length > 0 ? p.channelName : null,
        });
    }

    out.sort((a, b) => (a.fireAt - b.fireAt) || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    return out;
}
